import React, { useEffect, useRef } from 'react';
import styles from './App.module.scss';
import { Header } from '../components/header/Header';
import { Routes } from '../routes';
import { Footer } from '../components/footer/Footer';
import { SideBarMenu } from '../components/sideBarMenu/SideBarMenu';
import { User } from '../models/user';
import { useTranslation } from 'react-i18next';
import { SemanticToastContainer } from 'react-semantic-toasts';
import { MessageModal } from '../components/statusModal/MessageModal';
import { useAppStateMessageModalSelector } from '../components/statusModal/messageModalViewSlice';
import { SystemAlert } from '../components/systemAlert/SystemAlert';
import { useDispatch } from 'react-redux';
import { loadUserAlerts } from '../features/systemAlerts/systemAlertsActionCreators';
import { getFeatureFlags } from '../features/featureFlags/featureFlagActionCreator';
import { useAppStateSystemAlertsListSelector } from '../features/systemAlerts/systemAlertsListSlice';
import { NotificationSettings } from '../components/notificationSettings/NotificationSettings';
import { useFeatureFlag } from '../features/featureFlags/FeatureFlag';
import CitibotChat from '../components/citiBot/CitibotChat';
import { authService } from '../services/authService';
import { useAppStateLoginSelector } from '../features/login/loginSlice';
import MaintenanceBanner from '../components/systemAlert/MaintenanceBanner';
import { changeLanguage } from '../components/header/headerActionCreators';
import { useAppStateHeaderSelector } from '../components/header/headerSlice';
import { RouteAnnouncer } from '../components/routeAnnouncer/RouteAnnouncer';

export interface AppProps {
    isAuthenticated: boolean;
    authUser: User | null;
    selectedOrganization: string;
}

export const App: React.FunctionComponent<AppProps> = ({
    isAuthenticated,
    authUser,
    selectedOrganization,
}) => {
    const { i18n, t } = useTranslation();
    const dispatch = useDispatch();
    const languageInitialized = useRef(false);
    const { loading, unreadAlertIndex } = useAppStateSystemAlertsListSelector(
        (state) => state,
    );
    const { languageOptions } = useAppStateHeaderSelector((state) => state);
    const delinquentBillingNotificationsFF = useFeatureFlag(
        'EnableDelinquentBillingNotifications',
    );
    const { setUpdatingLoginRecord } = useAppStateLoginSelector(
        (state) => state,
    );

    // Get supported languages from Redux state (loaded from database)
    const supportedLanguages = languageOptions.map((option: any) => option.value);

    const initializeLanguage = () => {
        console.log('=== initializeLanguage called ===');
        console.log('isAuthenticated:', isAuthenticated);
        console.log('languageInitialized.current:', languageInitialized.current);
        console.log('current i18n.language:', i18n.language);
        console.log('supportedLanguages from DB:', supportedLanguages);
        console.log('languageOptions loaded:', languageOptions.length > 0);
        
        // Wait for language options to be loaded from database
        if (languageOptions.length === 0) {
            console.log('Language options not loaded yet, skipping initialization');
            return;
        }
        
        // Reset initialization flag when authentication state changes
        const authStateKey = `authState_${isAuthenticated}_${authUser?.Id || 'none'}`;
        const lastAuthState = sessionStorage.getItem('lastAuthState');
        
        if (lastAuthState !== authStateKey) {
            console.log('Authentication state changed, resetting language initialization');
            console.log('Previous auth state:', lastAuthState);
            console.log('New auth state:', authStateKey);
            languageInitialized.current = false;
            sessionStorage.removeItem('languageInitialized');
            sessionStorage.setItem('lastAuthState', authStateKey);
        }

        // Helper function to detect browser language
        const detectBrowserLanguage = () => {
            const userLang = navigator.language;
            console.log('Using browser language detection:', userLang);

            if (!userLang) {
                return 'en';
            }
            
            const userLanguageCleaned = userLang.split('-')[0];
            console.log('Cleaned browser language:', userLanguageCleaned);
            
            const supportedLanguageDetected = supportedLanguages.find(
                (language: string) => language === userLanguageCleaned,
            );
            
            return supportedLanguageDetected || 'en';
        };

        // Helper function to set language and log the decision
        const setLanguageWithLog = (language: string, reason: string) => {
            console.log(`Setting language to '${language}': ${reason}`);
            dispatch(changeLanguage(language));
            languageInitialized.current = true;
            sessionStorage.setItem('languageInitialized', 'true');
        };

        // If user has explicitly set their language preference (forceUpdateLanguage = true),
        // use their database language preference instead of browser detection
        if (!isAuthenticated) {
            // For unauthenticated users, check localStorage for stored language preference
            const storedLanguage = localStorage.getItem('language');
            const isLanguageOverwrittenByDropdown = localStorage.getItem('isLanguageOverwrittenByDropdown') === 'true';
            
            console.log('Unauthenticated user - storedLanguage:', storedLanguage, 'isLanguageOverwrittenByDropdown:', isLanguageOverwrittenByDropdown);
            
            if (isLanguageOverwrittenByDropdown && storedLanguage && supportedLanguages.includes(storedLanguage)) {
                setLanguageWithLog(storedLanguage, 'stored language preference for unauthenticated user (explicitly set)');
            } else {
                const detectedLanguage = detectBrowserLanguage();
                setLanguageWithLog(detectedLanguage, 'browser language detection for unauthenticated user');
            }
        } else {
            // For authenticated users, check if they made an explicit choice while unauthenticated FIRST
            const storedLanguage = localStorage.getItem('language');
            const isLanguageOverwrittenByDropdown = localStorage.getItem('isLanguageOverwrittenByDropdown') === 'true';
            
            console.log('Authenticated user - storedLanguage:', storedLanguage, 'isLanguageOverwrittenByDropdown:', isLanguageOverwrittenByDropdown);
            console.log('Database language:', authUser?.Language, 'isLanguageOverwrittenByDropdown:', authUser?.isLanguageOverwrittenByDropdown);
            
            // PRIORITY: If user explicitly set language while unauthenticated, use that preference FIRST
            if (isLanguageOverwrittenByDropdown && storedLanguage && supportedLanguages.includes(storedLanguage)) {
                console.log('Using localStorage preference from unauthenticated session');
                setLanguageWithLog(storedLanguage, 'user\'s explicit choice made while unauthenticated');
            } else if (authUser?.isLanguageOverwrittenByDropdown && authUser?.Language) {
                // SECOND PRIORITY: If user explicitly set language in database, use that preference
                console.log('Using database preference (no localStorage preference found)');
                setLanguageWithLog(authUser.Language, 'user\'s explicit database preference');
            } else {
                // Use browser language detection as fallback
                console.log('No explicit preference found, using browser detection');
                console.log('Using browser language detection for authenticated user');
                console.log('The language is: ' + navigator.language);
                
                const detectedLanguage = detectBrowserLanguage();
                setLanguageWithLog(detectedLanguage, 'browser language detection for authenticated user');
            }
        }
    };

    useEffect(() => {
        // Record user session if the user hasn't just logged in
        if (authUser?.Id && !setUpdatingLoginRecord) {
            authService.recordUserSession(false);
        }

        // Load system alerts and bill maintenance periods
        dispatch(loadUserAlerts(t));

        // Fetch feature flags
        dispatch(getFeatureFlags(authUser?.Id));

        // eslint-disable-next-line
    }, [dispatch, authUser, isAuthenticated]);

    // Separate useEffect for language initialization that waits for language options
    useEffect(() => {
        if (languageOptions.length > 0) {
            console.log('Language options loaded, initializing language...');
            initializeLanguage();
        }
    // eslint-disable-next-line
    }, [languageOptions, authUser, isAuthenticated]);

    // Sync HTML lang attribute with current i18n language
    useEffect(() => {
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    const isVerified = authUser?.Verified || false;

    // simple popup messages (alternative to toast messages)
    const { messageModalOpen } = useAppStateMessageModalSelector(
        (state) => state,
    );

    return (
        <div className={styles.app}>
            {/* Skip link for keyboard navigation */}
            <nav aria-label="Skip links">
                <a href="#main-content" className={styles.skipLink}>
                    {t('Accessibility.SkipToMainContent', 'Skip to main content')}
                </a>
            </nav>
            <RouteAnnouncer />

            <Header isSignedIn={isAuthenticated} />
            <SystemAlert />
            <div aria-live="polite" role="status">
                <SemanticToastContainer position="top-right" />
            </div>
            <MessageModal modalOpen={messageModalOpen} />
            <CitibotChat />
            <section aria-label="Maintenance notices">
                <MaintenanceBanner />
            </section>

            {isAuthenticated &&
            delinquentBillingNotificationsFF?.enabled &&
            !loading &&
            unreadAlertIndex === -1 ? (
                <NotificationSettings
                    userId={authUser?.Id!}
                    asModal={true}
                    viewOnly={false}
                />
            ) : null}

            <main id="main-content" role="main">
                {isAuthenticated && authUser!.SalesforceId ? (
                    <div className={`${styles.authenticatedRouteContainer}`}>
                        <div className={styles.authenticatedScrollView}>
                            <SideBarMenu />
                            <div className={styles.routeContainer}>
                                <Routes
                                    isAuthenticated={isAuthenticated}
                                    selectedOrganization={selectedOrganization}
                                    isVerified={isVerified}
                                    hasSalesforceId
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.unauthenticatedRouteContainer}>
                        <Routes
                            isAuthenticated={isAuthenticated}
                            selectedOrganization={selectedOrganization}
                            isVerified={isVerified}
                        />
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};
